import React, { useState } from 'react';
import { toast } from 'sonner';
import { PlayerTile } from './PlayerTile';

const plural = (n) => (n !== 1 ? 's' : '');

function SubPickerFooter({ stage, outCount, inCount, onBack, onNext, onConfirm }) {
  const btn = 'px-5 py-2.5 rounded-full font-semibold text-sm bg-emerald-500 text-[#0A0F2C] disabled:opacity-40 disabled:cursor-not-allowed';
  return (
    <div className="border-t border-white/10 px-4 sm:px-6 py-3 flex items-center justify-between gap-3 bg-[#0A0F2C]">
      {stage === 'in' ? (
        <button onClick={onBack}
          className="text-white/70 hover:text-white text-sm font-medium underline underline-offset-4"
          data-testid="sub-picker-back-btn">
          ← Back to OUT
        </button>
      ) : <span />}
      {stage === 'out' ? (
        <button onClick={onNext} disabled={outCount === 0} className={btn} data-testid="sub-picker-next-btn">
          Next: pick sub-ins ({outCount}) →
        </button>
      ) : (
        <button onClick={onConfirm} disabled={inCount !== outCount} className={btn} data-testid="sub-picker-confirm-btn">
          Confirm {outCount} substitution{plural(outCount)}
        </button>
      )}
    </div>
  );
}

// Multi-select OUT stage, then pick the same number IN; committed as one lineup POST.
export function SubPicker({ team, onCourt, bench, onCancel, onSwap }) {
  const [stage, setStage] = useState('out');
  const [outIds, setOutIds] = useState([]);
  const [inIds, setInIds] = useState([]);

  const toggleOut = (pid) => setOutIds(prev => prev.includes(pid) ? prev.filter(x => x !== pid) : [...prev, pid]);
  const toggleIn = (pid) => setInIds(prev => {
    if (prev.includes(pid)) return prev.filter(x => x !== pid);
    if (prev.length >= outIds.length) {
      toast.info(`Pick exactly ${outIds.length} player${plural(outIds.length)} to sub in`);
      return prev;
    }
    return [...prev, pid];
  });

  const goToInStage = () => {
    if (outIds.length === 0) { toast.error('Pick at least one player to sub out'); return; }
    setStage('in');
  };
  const confirm = () => {
    if (inIds.length !== outIds.length) {
      toast.error(`Pick ${outIds.length} player${plural(outIds.length)} to sub in`);
      return;
    }
    onSwap(outIds, inIds);
  };

  const isOut = stage === 'out';
  const roster = isOut ? onCourt : bench;
  const selectedIds = isOut ? outIds : inIds;
  const toggleFn = isOut ? toggleOut : toggleIn;

  return (
    <div className="fixed inset-0 z-[105] bg-[#0A0F2C]/95 backdrop-blur-sm flex flex-col" data-testid="sub-picker">
      <div className="flex items-center justify-between px-4 sm:px-6 pt-4 pb-2 gap-4">
        <div className="text-white text-lg sm:text-2xl font-semibold leading-tight">
          {isOut
            ? 'Sub OUT — tap every player leaving the court'
            : `Sub IN — pick ${outIds.length} player${plural(outIds.length)} from the bench`}
        </div>
        <button onClick={onCancel} className="text-white/70 hover:text-white text-sm font-medium flex-shrink-0" data-testid="sub-picker-cancel">
          cancel
        </button>
      </div>
      <div className="flex items-center justify-between px-4 sm:px-6 mb-3">
        <div className="text-amber-300 text-xs sm:text-sm">{team.name}</div>
        <div className="text-white/60 text-xs sm:text-sm" data-testid="sub-picker-counter">
          {isOut ? `${outIds.length} selected` : `${inIds.length} / ${outIds.length}`}
        </div>
      </div>
      <div className="flex-1 px-4 sm:px-6 pb-2 overflow-y-auto">
        <div className="w-full max-w-5xl grid grid-cols-3 gap-3 sm:gap-4 mx-auto">
          {roster.map(p => (
            <PlayerTile
              key={p.id}
              player={p}
              selected={selectedIds.includes(p.id)}
              onClick={() => toggleFn(p.id)}
              testId={`sub-${stage}-${p.id}`}
            />
          ))}
          {roster.length === 0 && (
            <div className="col-span-3 text-center text-white/60 py-10">
              {isOut ? 'Nobody is on court yet.' : 'Everyone is already on court.'}
            </div>
          )}
        </div>
      </div>
      <SubPickerFooter
        stage={stage}
        outCount={outIds.length}
        inCount={inIds.length}
        onBack={() => { setStage('out'); setInIds([]); }}
        onNext={goToInStage}
        onConfirm={confirm}
      />
    </div>
  );
}
